import Button from "~/components/Button/Button";
import EditIcon from "~/icons/EditIcon";
import MinusIcon from "~/icons/MinusIcon";
import PlusIcon from "~/icons/PlusIcon";
import {
  getFinalPrice,
  getModifiersList,
  getTotalPrice,
} from "~/helpers/cartHelpers";

import React, { useContext, useMemo } from "react";
import ThemeContext from "~/contexts/ThemeContext";
import {
  CartItemModifiersFontSize,
  CartItemNameFontSize,
  CartItemPriceFontSize,
} from "~/theme/models/themeModels";
import TaxContext from "~/contexts/TaxContext";

import styles from "./CartItem.module.css";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import RemoveIcon from "~/icons/RemoveIcon";
import { CartLine, Discount, OrderSource } from "~/entities/orderEntities";
import OrderContext from "~/contexts/OrderContext";

type OrderCartItemProps = {
  line: CartLine;
  updateLine: (line: CartLine) => void;
  openEditLineModal: VoidFunction;
  orderSource: OrderSource;
};

const CartLineView: React.FC<OrderCartItemProps> = ({
  line,
  updateLine,
  openEditLineModal,
}) => {
  const { template } = useContext(ThemeContext);
  const { formatAmount } = useContext(TaxContext);
  const { readonlyCart } = useContext(OrderContext);

  const product = line.item.product;

  const modifiers = useMemo(() => getModifiersList(line.item), [line]);

  // Styles
  const backgroundColor = template.cart.backgroundColour;
  const fontColour = template.cart.fontColour;
  const imageBackgroundColour = template.cart.item.imageBackgroundColour;
  const cartItemNameFontSize =
    CartItemNameFontSize[template.cart.item.nameFontSize];
  const cartItemNameFontColor = template.cart.item.nameFontColour;
  const cartItemModifiersFontSize =
    CartItemModifiersFontSize[template.cart.item.modifiersFontSize];
  const cartItemModifiersFontColor = template.cart.item.modifiersFontColour;
  const cartItemPriceFontSize =
    CartItemPriceFontSize[template.cart.item.priceFontSize];
  const cartItemPriceFontColor = template.cart.item.priceFontColour;

  const totalPrice = getTotalPrice(line.item);
  const finalPrice = getFinalPrice(line.item);

  const getDiscounts = (discounts: Discount[]) => {
    const amounts: {
      label: string;
      amount: ReturnType<typeof formatAmount>;
    }[] = [];

    for (const discount of discounts) {
      const discountAmount = [
        discount.percentTimes1000
          ? `-${discount.percentTimes1000 / 1000}%`
          : null,
        discount.fixedAmount ? `-${formatAmount(discount.fixedAmount)}` : null,
      ]
        .filter(Boolean)
        .join(" and ");

      amounts.push({
        label: discount.name,
        amount: discountAmount,
      });
    }

    return amounts;
  };

  return (
    <div
      className={styles.wrapper}
      style={{ backgroundColor, color: fontColour }}
    >
      <div
        className={styles.imageWrapper}
        style={{ backgroundColor: imageBackgroundColour }}
      >
        {product.image && (
          <img
            className={styles.image}
            src={uploadcareConfig.getUrl(
              product.image.fileId,
              product.image.modifiers,
            )}
            alt={product.image.fileName}
          />
        )}
      </div>
      <div className={styles.productDetails}>
        <span
          className={styles.productName}
          style={{
            fontSize: cartItemNameFontSize,
            color: cartItemNameFontColor,
          }}
        >
          {product.name}
        </span>
        <span
          className={styles.productModifiers}
          style={{
            fontSize: cartItemModifiersFontSize,
            color: cartItemModifiersFontColor,
          }}
        >
          {modifiers}
        </span>
        <span
          className={styles.editButton}
          style={{ color: `${fontColour}99` }}
          onClick={() => openEditLineModal()}
        >
          <EditIcon color={`${fontColour}99`} />
          <span>Edit</span>
        </span>
      </div>
      <div className={styles.actions}>
        <div className={styles.counterWrapper}>
          <Button
            variant="counter-secondary"
            disabled={readonlyCart}
            onClick={() =>
              updateLine({
                ...line,
                item: {
                  ...line.item,
                  qty: Math.max(line.item.qty - 1, 0),
                },
              })
            }
          >
            {line.item.qty === 1 ? <RemoveIcon size={16} /> : <MinusIcon />}
          </Button>
          <div className={styles.count}>{line.item.qty}</div>
          <Button
            variant="counter-primary"
            onClick={() =>
              updateLine({
                ...line,
                item: { ...line.item, qty: line.item.qty + 1 },
              })
            }
            disabled={line.item.product.soldOut || readonlyCart}
          >
            <PlusIcon />
          </Button>
        </div>
        <div className={styles.priceWrapper}>
          {totalPrice !== finalPrice && (
            <span
              className={styles.productPrice}
              style={{
                fontSize: cartItemPriceFontSize,
                color: cartItemPriceFontColor,
                textDecoration: "line-through",
                marginTop: "auto",
              }}
            >
              {formatAmount(totalPrice)}
            </span>
          )}
          <span
            className={styles.productFinalPrice}
            style={{
              fontSize: cartItemPriceFontSize,
              color: cartItemPriceFontColor,
            }}
          >
            {formatAmount(finalPrice)}
          </span>
        </div>
      </div>
      <div className={styles.discounts}>
        {getDiscounts([...line.discounts, ...line.item.discounts]).map(
          (discount) => (
            <div key={discount.label} className={styles.discountTag}>
              {discount.label} {discount.amount}
            </div>
          ),
        )}
      </div>
    </div>
  );
};

export default CartLineView;
